import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styles from './GearDetailsPage.module.css';
import API_BASE_URL from '../config/api';
import { Helmet } from 'react-helmet-async';
import { useUser } from '../context/UserContext';
import { useTranslation } from 'react-i18next';
import TranslateToggle from '../components/TranslateToggle';
import GearDetailsPageSkeleton from '../components/GearDetailsPageSkeleton';
import { addToCompare, clearCompare, COMPARE_LIMIT, getCompareIds } from '../utils/gearCompare';
import { getCertificationAnchorId } from '../config/certifications';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrencyAmount } from '../utils/formatCurrency';
import { getPriceSettings, PRICE_SETTINGS_STORAGE_KEY } from '../utils/priceSettings';

const COMMENT_MAX_LENGTH = 750;

const parseNumeric = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number.parseFloat(String(value || '').replace(',', '.').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const PROPERTY_LABELS = {
  // Universal
  weight: 'Weight',
  gender: 'Gender',
  material_composition: 'Material',
  certifications: 'Certification',
  
  // Footwear
  boot_category: 'Boot Category',
  crampon_compat: 'Crampon Compat.',
  waterproofing: 'Waterproofing',
  water_resistance: 'Water Resistance',
  sole_type: 'Sole Type',
  upper_material: 'Upper Material',
  
  // Hardshell
  waterproofness: 'Waterproofness (mm)',
  breathability: 'Breathability',
  construction: 'Construction',
  helmet_compatible: 'Helmet Compatible',
  
  // Insulation
  material_type: 'Insulation Type',
  fill_power: 'Fill Power',
  fill_weight: 'Fill Weight',
  rds_certified: 'RDS Certified',
  
  // Baselayer
  fabric_weight: 'Fabric Weight',
  material_base: 'Material',
  odor_resistance: 'Odor Resistance',
  fiber_micron: 'Fiber Micron',
  baselayer_type: 'Baselayer Type',
  baselayer_subtype: 'Baselayer Style',
  
  // Crampons
  binding_type: 'Binding Type',
  material: 'Material',
  points: 'Points',
  front_points: 'Front Points',
  antibott_included: 'Antibott Included',
  
  // Ice Axes
  technical_rating: 'Rating',
  shaft_shape: 'Shaft Shape',
  head_type: 'Head Type',
  length: 'Length',
  leash_included: 'Leash Included',
  
  // Ropes
  rope_type: 'Rope Type',
  diameter: 'Diameter',
  uiaa_falls: 'UIAA Falls',
  elongation_dynamic: 'Dynamic Elongation',
  dry_treatment: 'Dry Treated',
  
  // Carabiners
  major_axis_strength: 'Strength (Major Axis)',
  gate_type: 'Gate Type',
  rating: 'Rating',
  
  // Backpacks
  capacity: 'Capacity',
  frame_type: 'Frame Type',
  hydration_compatible: 'Hydration Compatible',
  rain_cover: 'Rain Cover',
  hip_belt: 'Hip Belt',
  sternum_strap: 'Sternum Strap',
  intended_activity: 'Intended Activity',
  access_style: 'Access Style',
  helmet_carry: 'Helmet Carry',
  laptop_sleeve: 'Laptop Sleeve',
  trekking_pole_carry: 'Trekking Pole Carry',
  ice_axe_carry: 'Ice Axe Carry',
  
  // Tents
  person_capacity: 'Capacity',
  min_weight: 'Min Weight',
  waterhead_rating: 'Waterhead Rating',
  
  // Trekking Poles
  grip_material: 'Grip Material',
  locking_mechanism: 'Locking Mechanism',
  min_length: 'Collapsed Length',
  max_length: 'Max Length',
  
  // Electronics / Headlamps
  lumens: 'Max Lumens',
  battery_type: 'Battery Type',
  red_light: 'Red Light Mode',
  
  // Helmets
  ventilation: 'Ventilation',
  headlamp_clips: 'Headlamp Clips',
  
  // Harnesses
  gear_loops: 'Gear Loops',
  leg_loop_type: 'Leg Loops',
  ice_clipper_slots: 'Ice Clipper Slots',
  
  // Sleeping Bags
  temp_comfort: 'Comfort Temp',
  temp_limit: 'Limit Temp',
  temp_extreme: 'Extreme Temp',
  
  // Avalanche
  frequency: 'Frequency',
  search_strip_width: 'Search Strip Width',
  marking_function: 'Marking Function'
};

function GearDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, addToGearWatchlist, removeFromGearWatchlist } = useUser();
  const { t, i18n } = useTranslation();
  const [gear, setGear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [avgRatings, setAvgRatings] = useState({ avgComfort: 0, avgQuality: 0, avgPricePerformance: 0, count: 0 });
  const [userRatings, setUserRatings] = useState({ comfort: '', quality: '', pricePerformance: '' });
  const [ratingError, setRatingError] = useState(null);
  const [ratingSuccess, setRatingSuccess] = useState(false);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentListError, setCommentListError] = useState(null);
  const [commentFormError, setCommentFormError] = useState(null);
  const [commentSuccess, setCommentSuccess] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentDirty, setCommentDirty] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [compareMessage, setCompareMessage] = useState('');
  const [watchlistMessage, setWatchlistMessage] = useState('');
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [priceAnalytics, setPriceAnalytics] = useState(null);
  const [showAnalyticsPopup, setShowAnalyticsPopup] = useState(false);
  const [priceSettings, setPriceSettings] = useState(() => getPriceSettings());
  const textareaRef = useRef(null);
  const commentSuccessTimeoutRef = useRef(null);

  const storedUser = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem('currentUser');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.warn('Failed to parse stored user', e);
      return null;
    }
  }, []);

  const activeUser = currentUser || storedUser;
  const authToken = activeUser?.token;
  const activeUsername = activeUser?.username || null;
  const canModerateComments = !!activeUser?.admin || !!activeUser?.editor;
  const isLoggedIn = Boolean(authToken);
  const isInWatchlist = useMemo(() => {
    if (!gear?.id) return false;
    const watchlistSet = new Set((currentUser?.gearWatchlist || []).map((entryId) => String(entryId || '').trim()));
    return watchlistSet.has(String(gear.id).trim());
  }, [currentUser?.gearWatchlist, gear?.id]);

  const helmetTitle = gear
    ? t('gearDetails.helmetTitleDynamic', { name: gear.name, brand: gear.brand })
    : t('gearDetails.helmetTitle');
  const helmetDescription = gear
    ? t('gearDetails.helmetDescriptionDynamic', { name: gear.name, brand: gear.brand })
    : t('gearDetails.helmetDescription');

  const priceOffers = useMemo(() => {
    if (!Array.isArray(gear?.offers)) return [];

    const offers = gear.offers
      .map((offer, index) => {
        const parsedPrice = parseNumeric(offer?.price);

        return {
          id: offer?.offerId || `${offer?.seller || 'offer'}-${index}`,
          seller: offer?.seller || t('gearDetails.unknownMerchant', { defaultValue: 'Unknown Store' }),
          currency: offer?.currency || gear?.currency || 'EUR',
          price: Number.isFinite(parsedPrice) ? parsedPrice : null,
          approxPriceEUR: parseNumeric(offer?.approxPriceEUR),
          approxPriceInTargetCurrency: parseNumeric(offer?.approxPriceInTargetCurrency),
          url: offer?.url || ''
        };
      })
      .filter((offer) => offer.price !== null)
      .sort((a, b) => a.price - b.price);

    const uniqueOffers = [];
    const seen = new Set();
    for (const offer of offers) {
      const key = `${offer.seller}-${offer.price}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueOffers.push(offer);
      }
    }
    return uniqueOffers;
  }, [gear, t]);

  const filteredPriceOffers = useMemo(() => {
    const selectedCurrencies = new Set(priceSettings.selectedPriceCurrencies);
    const filtered = priceOffers.filter((offer) => selectedCurrencies.has(String(offer.currency || '').toUpperCase()));
    if (priceSettings.viewMode !== 'prices') return priceOffers;
    return filtered.length > 0 ? filtered : priceOffers;
  }, [priceOffers, priceSettings.selectedPriceCurrencies, priceSettings.viewMode]);

  const displayPriceRows = useMemo(() => {
    const targetCurrency = priceSettings.exchangeTargetCurrency;

    const rows = filteredPriceOffers.map((offer) => {
      const originalFormatted = formatCurrencyAmount(offer.price, offer.currency, i18n.language);

      if (priceSettings.viewMode === 'prices') {
        return {
          ...offer,
          mainValue: originalFormatted,
          secondaryValue: '',
          compareValue: offer.price
        };
      }

      if (priceSettings.viewMode === 'exchange') {
        const convertedValue = Number.isFinite(offer.approxPriceInTargetCurrency)
          ? offer.approxPriceInTargetCurrency
          : (String(offer.currency || '').toUpperCase() === targetCurrency ? offer.price : null);

        if (Number.isFinite(convertedValue)) {
          return {
            ...offer,
            mainValue: formatCurrencyAmount(convertedValue, targetCurrency, i18n.language),
            secondaryValue: originalFormatted,
            compareValue: convertedValue
          };
        }
      }

      return {
        ...offer,
        mainValue: originalFormatted,
        secondaryValue: '',
        compareValue: offer.price
      };
    });

    if (priceSettings.viewMode === 'exchange') {
      return rows.slice().sort((a, b) => (a.compareValue ?? Number.POSITIVE_INFINITY) - (b.compareValue ?? Number.POSITIVE_INFINITY));
    }

    return rows;
  }, [filteredPriceOffers, i18n.language, priceSettings]);

  const galleryImages = useMemo(() => {
    if (!gear) return [];

    const buildImageDedupKey = (candidate) => {
      const normalized = String(candidate || '').trim();
      if (!normalized) return '';
      if (!(normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('//'))) return '';

      try {
        const absoluteUrl = normalized.startsWith('//') ? `https:${normalized}` : normalized;
        const parsed = new URL(absoluteUrl);
        const host = parsed.hostname.toLowerCase();
        const lowerPath = String(parsed.pathname || '/').toLowerCase();
        const sizedPath = lowerPath.replace(/([_-])\d{2,4}x\d{2,4}(?=\.[a-z0-9]+$)/i, '');
        return `${host}${sizedPath}`;
      } catch {
        return normalized
          .toLowerCase()
          .split('#')[0]
          .split('?')[0]
          .replace(/([_-])\d{2,4}x\d{2,4}(?=\.[a-z0-9]+$)/i, '');
      }
    };

    const unique = new Map();
    const add = (candidate) => {
      const normalized = String(candidate || '').trim();
      if (!normalized) return;
      if (!(normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('//'))) return;
      const key = buildImageDedupKey(normalized);
      if (!key) return;
      if (!unique.has(key)) {
        unique.set(key, normalized);
      }
    };

    if (Array.isArray(gear.images)) {
      gear.images.forEach((image) => add(image));
    }

    add(gear.image);

    if (Array.isArray(gear.offers)) {
      gear.offers.forEach((offer) => {
        add(offer?.imageUrl);
        add(offer?.image);
      });
    }

    return Array.from(unique.values());
  }, [gear]);

  const currentImageIndex = useMemo(() => {
    if (!selectedImage || !galleryImages.length) return 0;
    const idx = galleryImages.indexOf(selectedImage);
    return idx >= 0 ? idx : 0;
  }, [selectedImage, galleryImages]);

  const handlePrevImage = () => {
    if (!galleryImages.length) return;
    const prev = (currentImageIndex - 1 + galleryImages.length) % galleryImages.length;
    setSelectedImage(galleryImages[prev]);
  };

  const handleNextImage = () => {
    if (!galleryImages.length) return;
    const next = (currentImageIndex + 1) % galleryImages.length;
    setSelectedImage(galleryImages[next]);
  };
  const SELLER_COLORS = ['#ff6b6b', '#51cf66', '#fcc419', '#845ef7', '#ff922b', '#22b8cf', '#e64980', '#20c997'];

  // Build a synthetic currentWeek from offers when no backend analytics exist
  const effectiveAnalytics = useMemo(() => {
    let baseAnalytics = priceAnalytics;

    if (baseAnalytics && priceSettings.viewMode === 'prices' && priceSettings.selectedPriceCurrencies?.length > 0) {
      const allowed = new Set(priceSettings.selectedPriceCurrencies.map(c => c.toUpperCase()));
      const isAllowed = (sellerKey) => {
        if (!sellerKey) return false;
        const parts = sellerKey.split(' ');
        const curr = parts[parts.length - 1].toUpperCase();
        return allowed.has(curr);
      };
      const filterObj = (obj) => {
        if (!obj) return obj;
        const res = {};
        for (const [k, v] of Object.entries(obj)) {
          if (isAllowed(k)) res[k] = v;
        }
        return res;
      };

      const currentWeek = { ...baseAnalytics.currentWeek };
      currentWeek.sellerLows = filterObj(currentWeek.sellerLows);
      if (currentWeek.volatility) {
        currentWeek.volatility = { ...currentWeek.volatility, sellerChanges: filterObj(currentWeek.volatility.sellerChanges) };
      }
      if (currentWeek.trustSignals) {
        currentWeek.trustSignals = {
          ...currentWeek.trustSignals,
          sellerPromoClaims: filterObj(currentWeek.trustSignals.sellerPromoClaims),
          sellerPromoVerified: filterObj(currentWeek.trustSignals.sellerPromoVerified)
        };
      }
      
      let minPrice = Infinity;
      for (const p of Object.values(currentWeek.sellerLows || {})) {
        if (p < minPrice) minPrice = p;
      }
      currentWeek.lowestPrice = minPrice === Infinity ? null : minPrice;

      const history = (baseAnalytics.history || []).map(week => {
        const w = { ...week, sellerLows: filterObj(week.sellerLows) };
        let wp = Infinity;
        for (const p of Object.values(w.sellerLows || {})) {
          if (p < wp) wp = p;
        }
        w.lowestPrice = wp === Infinity ? null : wp;
        if (w.volatility) {
          w.volatility = { ...w.volatility, sellerChanges: filterObj(w.volatility.sellerChanges) };
        }
        if (w.trustScore) {
          w.trustScore = filterObj(w.trustScore);
        }
        return w;
      });

      const trustScores = filterObj(baseAnalytics.trustScores);

      const leaderboard = { ...baseAnalytics.leaderboard };
      if (leaderboard.priceStabilityRank) {
        leaderboard.priceStabilityRank = leaderboard.priceStabilityRank.filter(isAllowed);
      }
      if (leaderboard.mostOftenCheapest && !isAllowed(leaderboard.mostOftenCheapest)) leaderboard.mostOftenCheapest = null;
      if (leaderboard.bestAvgDiscount && !isAllowed(leaderboard.bestAvgDiscount)) leaderboard.bestAvgDiscount = null;

      baseAnalytics = {
        ...baseAnalytics,
        currentWeek,
        history,
        trustScores,
        leaderboard
      };
    }

    if (baseAnalytics) return baseAnalytics;
    if (!filteredPriceOffers.length) return null;

    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    const weekKey = `${d.getUTCFullYear()}-W${weekNo.toString().padStart(2, '0')}`;

    const lowestPrice = filteredPriceOffers[0]?.price ?? null;
    const sellerLows = {};
    filteredPriceOffers.forEach(o => {
      const key = o.seller.trim().replace(/\s+/g, ' ').toLowerCase();
      if (key && o.price != null && (sellerLows[key] === undefined || o.price < sellerLows[key])) {
        sellerLows[key] = o.price;
      }
    });

    return {
      currentWeek: {
        weekKey,
        lowestPrice,
        sellerLows,
        volatility: { productChanges: 0, sellerChanges: {}, productRange: { min: lowestPrice, max: lowestPrice } },
        trustSignals: { sellerPromoClaims: {}, sellerPromoVerified: {} }
      },
      history: [],
      leaderboard: {},
      trustScores: {},
      _synthetic: true
    };
  }, [priceAnalytics, filteredPriceOffers, priceSettings.selectedPriceCurrencies, priceSettings.viewMode]);

  const analyticsChartData = useMemo(() => {
    if (!effectiveAnalytics) return { fullHistory: [], sellers: [], inlineHistory: [] };

    const weeks = [...(effectiveAnalytics.history || [])];
    if (effectiveAnalytics.currentWeek?.weekKey) {
      weeks.push({
        weekKey: effectiveAnalytics.currentWeek.weekKey,
        lowestPrice: effectiveAnalytics.currentWeek.lowestPrice,
        sellerLows: effectiveAnalytics.currentWeek.sellerLows || {},
      });
    }

    const sellerSet = new Set();
    weeks.forEach(w => Object.keys(w.sellerLows || {}).forEach(s => sellerSet.add(s)));
    const sellers = Array.from(sellerSet);

    const fullHistory = weeks.map(w => {
      const point = { weekKey: w.weekKey, lowestPrice: w.lowestPrice };
      sellers.forEach(s => { point[s] = w.sellerLows?.[s] ?? null; });
      return point;
    });

    const inlineHistory = fullHistory.slice(-3);

    return { fullHistory, sellers, inlineHistory };
  }, [effectiveAnalytics]);

  const currentWeekCheapest = useMemo(() => {
    if (!effectiveAnalytics?.currentWeek?.sellerLows) return null;
    const entries = Object.entries(effectiveAnalytics.currentWeek.sellerLows);
    if (!entries.length) return null;
    return entries.reduce((best, [seller, price]) =>
      price < best.price ? { seller, price } : best
    , { seller: entries[0][0], price: entries[0][1] });
  }, [effectiveAnalytics]);

  const analyticsCurrency = useMemo(() => {
    return String(
      priceOffers[0]?.currency
      || gear?.currency
      || 'EUR'
    ).toUpperCase();
  }, [priceOffers, gear]);

  const formattedPrice = gear
    ? (displayPriceRows[0]?.mainValue || formatCurrencyAmount(
      priceOffers[0]?.price ?? gear.price,
      priceOffers[0]?.currency || gear?.currency || 'EUR',
      i18n.language
    ))
    : '';
  const helmet = (
    <Helmet>
      <title>{helmetTitle}</title>
      <meta name="description" content={helmetDescription} />
    </Helmet>
  );

  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    setCommentListError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/gear/${id}/comments`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setComments(Array.isArray(data.data) ? data.data : []);
        } else {
          setCommentListError(data.message || t('gearDetails.errors.commentsLoad'));
        }
      } else {
        setCommentListError(t('gearDetails.errors.commentsLoad'));
      }
    } catch (err) {
      setCommentListError(err.message || t('gearDetails.errors.commentsLoad'));
    } finally {
      setCommentsLoading(false);
    }
  }, [id, t]);

  const myComment = useMemo(() => {
    if (!activeUsername) return null;
    return comments.find((c) => c.authorUsername === activeUsername) || null;
  }, [comments, activeUsername]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key && event.key !== PRICE_SETTINGS_STORAGE_KEY) return;
      setPriceSettings(getPriceSettings());
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    async function fetchGearAndRatings() {
      try {
        setLoading(true);
        setError(null);

        // Fetch gear details
        const requestedCurrency = priceSettings.viewMode === 'exchange'
          ? priceSettings.exchangeTargetCurrency
          : 'EUR';
        const gearRes = await fetch(`${API_BASE_URL}/api/gear?currency=${encodeURIComponent(requestedCurrency)}`);
        if (!gearRes.ok) {
          throw new Error(`HTTP error! status: ${gearRes.status}`);
        }
        const gearData = await gearRes.json();
        if (gearData.success) {
          const found = gearData.data.find(g => String(g.id) === String(id));
          setGear(found || null);
        } else {
          setError(gearData.message || t('gearDetails.errors.gearFetch'));
        }

        // Fetch gear ratings
        const ratingsRes = await fetch(`${API_BASE_URL}/api/gear/${id}/ratings`);
        if (ratingsRes.ok) {
          const ratingsData = await ratingsRes.json();
          if (ratingsData.success) {
            setAvgRatings(ratingsData.data);
          } else {
            console.error("Failed to fetch average ratings:", ratingsData.message);
          }
        }

        // Fetch price analytics
        try {
          const analyticsRes = await fetch(`${API_BASE_URL}/api/gear/${id}/analytics`);
          if (analyticsRes.ok) {
            const analyticsData = await analyticsRes.json();
            if (analyticsData.success && analyticsData.data) {
              setPriceAnalytics(analyticsData.data);
            }
          }
        } catch (analyticsErr) {
          console.warn('Price analytics fetch failed:', analyticsErr);
        }

      } catch (e) {
        setError(e.message || t('gearDetails.errors.gearFetch'));
      } finally {
        setLoading(false);
      }
    }
    fetchGearAndRatings();
  }, [id, t, priceSettings.exchangeTargetCurrency, priceSettings.viewMode]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    if (!isLoggedIn && showRatingForm) {
      setShowRatingForm(false);
    }
  }, [isLoggedIn, showRatingForm]);

  useEffect(() => {
    if (!commentDirty) {
      setCommentText(myComment ? myComment.text : '');
    }
  }, [myComment, commentDirty]);

  useEffect(() => () => {
    if (commentSuccessTimeoutRef.current) {
      clearTimeout(commentSuccessTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!galleryImages.length) {
      setSelectedImage('');
      return;
    }

    setSelectedImage((current) => (galleryImages.includes(current) ? current : galleryImages[0]));
  }, [galleryImages]);

  async function handleRatingSubmit(e) {
    e.preventDefault();
    setRatingError(null);
    setRatingSuccess(false);

    const token = authToken;

    if (!token) {
      setRatingError(t('gearDetails.errors.ratingLogin'));
      return;
    }

    const { comfort, quality, pricePerformance } = userRatings;
    if (
      !comfort || !quality || !pricePerformance ||
      Number(comfort) < 1 || Number(comfort) > 5 ||
      Number(quality) < 1 || Number(quality) > 5 ||
      Number(pricePerformance) < 1 || Number(pricePerformance) > 5
    ) {
      setRatingError(t('gearDetails.errors.ratingInvalid'));
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/gear/${id}/rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          comfort: Number(comfort),
          quality: Number(quality),
          pricePerformance: Number(pricePerformance),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setRatingSuccess(true);
          setUserRatings({ comfort: '', quality: '', pricePerformance: '' });
          setShowRatingForm(false);
          const ratingsRes = await fetch(`${API_BASE_URL}/api/gear/${id}/ratings`);
          if (ratingsRes.ok) {
            const ratingsData = await ratingsRes.json();
            if (ratingsData.success) {
              setAvgRatings(ratingsData.data);
            } else {
              console.error("Failed to re-fetch average ratings after submission:", ratingsData.message);
            }
          }
        } else {
          setRatingError(data.message || t('gearDetails.errors.ratingSubmit'));
        }
      } else {
        setRatingError(t('gearDetails.errors.ratingSubmit'));
      }
    } catch (e) {
      setRatingError(e.message || t('gearDetails.errors.ratingUnknown'));
    }
  }

  const handleCommentInputChange = (e) => {
    if (!commentDirty) {
      setCommentDirty(true);
    }
    setCommentText(e.target.value);
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setCommentFormError(null);
    setCommentSuccess('');

    if (!authToken) {
      setCommentFormError(t('gearDetails.errors.commentLogin'));
      return;
    }

    const trimmed = commentText.trim();
    if (!trimmed) {
      setCommentFormError(t('gearDetails.errors.commentEmpty'));
      return;
    }
      if (trimmed.length > COMMENT_MAX_LENGTH) {
        setCommentFormError(t('gearDetails.errors.commentTooLong', { limit: COMMENT_MAX_LENGTH }));
      return;
    }

    setCommentSubmitting(true);
    try {
      const isUpdating = Boolean(myComment);
      const endpoint = isUpdating
        ? `${API_BASE_URL}/api/gear/${id}/comments/${myComment.id}`
        : `${API_BASE_URL}/api/gear/${id}/comments`;
      const method = isUpdating ? 'PATCH' : 'POST';
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({ text: trimmed })
      });
      if (!res.ok) {
        throw new Error(t('gearDetails.errors.commentSave'));
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || t('gearDetails.errors.commentSave'));
      }
      setCommentDirty(false);
      setCommentSuccess(isUpdating ? t('gearDetails.commentSuccessUpdated') : t('gearDetails.commentSuccessCreated'));
      if (commentSuccessTimeoutRef.current) {
        clearTimeout(commentSuccessTimeoutRef.current);
      }
      commentSuccessTimeoutRef.current = setTimeout(() => setCommentSuccess(''), 4000);
      await fetchComments();
    } catch (err) {
      setCommentFormError(err.message || t('gearDetails.errors.commentSave'));
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    setCommentFormError(null);
    setCommentSuccess('');

    if (!authToken) {
      setCommentFormError(t('gearDetails.errors.commentDeleteLogin'));
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/gear/${id}/comments/${comment.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) {
        throw new Error(t('gearDetails.errors.commentDelete'));
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || t('gearDetails.errors.commentDelete'));
      }
      if (comment.authorUsername === activeUsername) {
        setCommentDirty(false);
        setCommentText('');
      }
      await fetchComments();
    } catch (err) {
      setCommentFormError(err.message || t('gearDetails.errors.commentDelete'));
    }
  };

  const handleStartEditComment = (comment) => {
    if (comment.authorUsername !== activeUsername) return;
    setCommentDirty(false);
    setCommentText(comment.text || '');
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const formatCommentTimestamp = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch (err) {
      return value;
    }
  };

  const handleAddToCompare = () => {
    if (!gear?.id) return;
    const result = addToCompare(gear.id, { category: gear.category });

    if (!result.ok && result.reason === 'incompatible') {
      const keepOnlyNewItem = window.confirm(
        `You can only compare items from the same category (${result.compareCategory || '-'}). Delete current compare items and add this one?`
      );

      if (!keepOnlyNewItem) {
        setCompareMessage('Please remove current compare items first.');
        return;
      }

      clearCompare();
      const retryResult = addToCompare(gear.id, { category: gear.category });
      if (retryResult.ok) {
        setCompareMessage(`Comparison reset. Added to compare (${retryResult.ids.length}/${COMPARE_LIMIT}).`);
      }
      return;
    }

    if (!result.ok && result.reason === 'limit') {
      setCompareMessage(`You can compare up to ${COMPARE_LIMIT} products.`);
      return;
    }

    if (result.reason === 'exists') {
      setCompareMessage(`Already in compare (${getCompareIds().length}/${COMPARE_LIMIT}).`);
      return;
    }

    setCompareMessage(`Added to compare (${result.ids.length}/${COMPARE_LIMIT}).`);
  };

  const handleToggleWatchlist = async () => {
    if (!gear?.id) return;

    if (!currentUser) {
      setWatchlistMessage('Log in to use your watchlist.');
      return;
    }
    
    if (currentUser && !currentUser.isVerified) {
      setWatchlistMessage('Verify your email to unlock Watchlist feature.');
      return;
    }

    setWatchlistLoading(true);
    try {
      const result = isInWatchlist
        ? await removeFromGearWatchlist(gear.id)
        : await addToGearWatchlist(gear.id);

      if (!result?.success) {
        setWatchlistMessage(result?.message || 'Could not update watchlist.');
        return;
      }

      const count = Array.isArray(result.data) ? result.data.length : null;
      if (isInWatchlist) {
        setWatchlistMessage(count === null ? 'Removed from watchlist.' : `Removed from watchlist (${count}/10).`);
      } else {
        setWatchlistMessage(count === null ? 'Added to watchlist.' : `Added to watchlist (${count}/10).`);
      }
    } finally {
      setWatchlistLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        {helmet}
        <GearDetailsPageSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        {helmet}
        <div className={`${styles.contentSection} ${styles.error}`}>{error}</div>
      </>
    );
  }

  if (!gear) {
    return (
      <>
        {helmet}
        <div className={styles.contentSection}>{t('gearDetails.notFound')}</div>
      </>
    );
  }

  // ------------------------------------------ RETURN STATEMENT ------------------------------------------
  return (
    <>
      {helmet}
      <div className={styles.contentSection}>
      <div className={styles.leftPanel}>
        <div className={styles.topActionsRow}>
          <button type="button" onClick={() => {
            if (window.history.state && window.history.state.idx > 0) {
              navigate(-1);
            } else {
              navigate('/gear');
            }
          }} className={`${styles.backLink} backButton`}>
            ← {t('gearDetails.back')}
          </button>
        </div>

        <div className={styles.productHeader}>
          <div className={styles.breadcrumb}>
            <Link to="/gear" className={styles.breadcrumbLink}>Gear</Link>
            {gear.gender && <><span className={styles.breadcrumbSep}>/</span><span>{gear.gender}</span></>}
            {gear.category && <><span className={styles.breadcrumbSep}>/</span><Link to={`/gear?category=${encodeURIComponent(gear.category)}`} className={styles.breadcrumbLink}>{gear.category}</Link></>}
          </div>
          <div className={styles.titleRowContainer}>
            <h1 className={styles.gearNameHeader}>{gear.name}</h1>
            <div className={styles.topActionsGroup}>
              <button 
                type="button" 
                className={`${styles.actionIconButton} ${isInWatchlist ? styles.activeAction : ''}`} 
                onClick={handleToggleWatchlist} 
                disabled={watchlistLoading}
                title={isInWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill={isInWatchlist ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <span>{watchlistLoading ? 'Updating' : 'Watchlist'}</span>
              </button>

              <button 
                type="button" 
                className={styles.actionIconButton} 
                onClick={handleAddToCompare}
                title="Add to compare"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>Compare</span>
              </button>
            </div>
          </div>
          <div className={styles.gearBrandHeader}>{gear.brand}</div>

          {(watchlistMessage || compareMessage) && (
            <div className={styles.actionMessagesRow}>
              {watchlistMessage && <span className={styles.actionMessage}>{watchlistMessage}</span>}
              {compareMessage && (
                <span className={styles.actionMessage}>
                  {compareMessage} <Link to="/gear/compare" className={styles.compareOpenLink}>Open compare</Link>
                </span>
              )}
            </div>
          )}
        </div>

        {galleryImages.length > 0 && (
          <div className={styles.imageGallery}>
            <div className={styles.imageFrame}>
              <img
                src={selectedImage || galleryImages[0]}
                alt={gear.name}
                className={styles.gearImage}
              />

              {galleryImages.length > 1 && (
                <>
                  <button
                    type="button"
                    className={`${styles.photoNavButton} ${styles.left}`}
                    onClick={handlePrevImage}
                    aria-label="Previous image"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`${styles.photoNavButton} ${styles.right}`}
                    onClick={handleNextImage}
                    aria-label="Next image"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </>
              )}
            </div>
            {galleryImages.length > 1 && (
              <div className={styles.thumbnailRow}>
                {galleryImages.map((imageUrl, index) => (
                  <button
                    key={`${imageUrl}-${index}`}
                    type="button"
                    className={`${styles.thumbnailButton} ${(selectedImage || galleryImages[0]) === imageUrl ? styles.thumbnailActive : ''}`}
                    onClick={() => setSelectedImage(imageUrl)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={imageUrl} alt={`${gear.name} ${index + 1}`} className={styles.thumbnailImage} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className={styles.gearMetaRow}>
          <div className={styles.gearMetaBlock}>
            <div className={styles.gearPrice}>{t('gearDetails.price', { value: formattedPrice || '--' })}</div>
            {displayPriceRows.length > 0 && (
              <div className={styles.priceList}>
                {displayPriceRows.map((offer, index) => {
                  const isLowest = index === 0;
                  return (
                    <div key={offer.id} className={`${styles.priceRow} ${isLowest ? styles.lowestPriceRow : ''}`}>
                      <div className={styles.priceInfo}>
                        <span className={styles.priceSource}>{offer.seller}</span>
                        <span className={`${styles.priceValue} ${isLowest ? styles.lowestPriceValue : ''}`}>
                          {offer.mainValue}
                        </span>
                        {offer.secondaryValue && <span className={styles.secondaryPriceValue}>{offer.secondaryValue}</span>}
                      </div>
                      {offer.url && (offer.url.startsWith('http') || offer.url.startsWith('//')) && (
                        <a
                          href={offer.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${styles.priceLink} ${isLowest ? styles.lowestPriceLink : ''}`}
                        >
                          {isLowest ? 'Buy Now' : 'Shop'}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!displayPriceRows.length && gear.merchant && <div className={styles.gearMerchant}>{gear.merchant}</div>}
            {gear.currency && <div className={styles.gearCurrency}>{gear.currency}</div>}
          </div>

          <div className={styles.ratingSummaryBox}>
            <h3 className={styles.ratingTitle}>{t('gearDetails.averageRatings')}</h3>
            {avgRatings?.count > 0 ? (
              <>
                <div className={styles.ratingSummaryRow}>
                  <span>{t('gearDetails.fields.comfort')}</span>
                  <strong>{Math.round(Number(avgRatings.avgComfort || 0))}</strong>
                </div>
                <div className={styles.ratingSummaryRow}>
                  <span>{t('gearDetails.fields.quality')}</span>
                  <strong>{Math.round(Number(avgRatings.avgQuality || 0))}</strong>
                </div>
                <div className={styles.ratingSummaryRow}>
                  <span>{t('gearDetails.fields.pricePerformance')}</span>
                  <strong>{Math.round(Number(avgRatings.avgPricePerformance || 0))}</strong>
                </div>
                <div className={styles.ratingCount}>{t('gearDetails.ratingCount', { count: avgRatings.count })}</div>
              </>
            ) : (
              <div className={styles.ratingCount}>{t('gearDetails.noRatings')}</div>
            )}

            {isLoggedIn ? (
              !showRatingForm ? (
                <button className={styles.rateTriggerButton} onClick={() => setShowRatingForm(true)}>
                  {t('gearDetails.rateTrigger')}
                </button>
              ) : (
                <div className={styles.rateGearSection}>
                  <h4 className={styles.rateGearTitle}>{t('gearDetails.rateTitle')}</h4>
                  <form onSubmit={handleRatingSubmit} className={styles.ratingForm}>
                    <div className={styles.ratingInputGroup}>
                      <label htmlFor="comfort">{t('gearDetails.fields.comfort')}:</label>
                      <input
                        type="number"
                        id="comfort"
                        min="1"
                        max="5"
                        value={userRatings.comfort}
                        onChange={(e) => setUserRatings({ ...userRatings, comfort: e.target.value })}
                        className={styles.ratingInput}
                      />
                    </div>
                    <div className={styles.ratingInputGroup}>
                      <label htmlFor="quality">{t('gearDetails.fields.quality')}:</label>
                      <input
                        type="number"
                        id="quality"
                        min="1"
                        max="5"
                        value={userRatings.quality}
                        onChange={(e) => setUserRatings({ ...userRatings, quality: e.target.value })}
                        className={styles.ratingInput}
                      />
                    </div>
                    <div className={styles.ratingInputGroup}>
                      <label htmlFor="pricePerformance">{t('gearDetails.fields.pricePerformance')}:</label>
                      <input
                        type="number"
                        id="pricePerformance"
                        min="1"
                        max="5"
                        value={userRatings.pricePerformance}
                        onChange={(e) => setUserRatings({ ...userRatings, pricePerformance: e.target.value })}
                        className={styles.ratingInput}
                      />
                    </div>
                    {ratingError && <div className={styles.error}>{ratingError}</div>}
                    {ratingSuccess && <div className={styles.successMessage}>{t('gearDetails.ratingSuccess')}</div>}
                    <div className={styles.ratingActions}>
                      <button type="button" className={styles.cancelRatingButton} onClick={() => setShowRatingForm(false)}>
                        {t('gearDetails.ratingCancel')}
                      </button>
                      <button type="submit" className={styles.submitRatingButton}>{t('gearDetails.ratingSubmit')}</button>
                    </div>
                  </form>
                </div>
              )
            ) : (
              <p className={styles.loginToRate}>{t('gearDetails.ratingLoginHint')}</p>
            )}
          </div>
        </div>

        {effectiveAnalytics && (
          <div className={styles.priceChartContainer}>
            <h4 className={styles.priceChartTitle}>Price History</h4>

            {currentWeekCheapest && (
              <div className={styles.currentWeekSummary}>
                <span className={styles.currentWeekLabel}>This week's lowest</span>
                <span className={styles.currentWeekPrice}>{formatCurrencyAmount(currentWeekCheapest.price, analyticsCurrency, i18n.language)}</span>
                <span className={styles.currentWeekSeller}>by {currentWeekCheapest.seller}</span>
              </div>
            )}

            {analyticsChartData.inlineHistory.length > 1 ? (
              <div className={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={analyticsChartData.inlineHistory}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis 
                      dataKey="weekKey" 
                      tick={{fontSize: 12, fill: '#666'}} 
                      tickFormatter={(val) => val.split('-')[1]} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      domain={['auto', 'auto']} 
                      tick={{fontSize: 12, fill: '#666'}}
                      tickFormatter={(val) => formatCurrencyAmount(val, analyticsCurrency, i18n.language)}
                      axisLine={false}
                      tickLine={false}
                      width={50}
                    />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrencyAmount(value, analyticsCurrency, i18n.language), name === 'lowestPrice' ? 'Lowest Price' : name]}
                      labelFormatter={(label) => `Week: ${label}`}
                      contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="lowestPrice" 
                      stroke="#007bff" 
                      strokeWidth={3}
                      dot={{r: 4, fill: '#007bff', strokeWidth: 2, stroke: '#fff'}}
                      activeDot={{r: 6}}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className={styles.trackingNotice}>Price tracking started. History chart will appear after the first weekly snapshot.</p>
            )}
            <button 
              className={styles.showMoreAnalyticsBtn}
              onClick={() => setShowAnalyticsPopup(true)}
            >
              Show detailed analytics
            </button>
          </div>
        )}

      </div>
      {/*----------------------------------------------- RIGHT COLOUMN ---------------------------------------------- */}
      <div className={styles.rightPanel}>
        <section className={styles.descriptionCard}>
          <h3>{t('gearDetails.description')}</h3>
          <div className={styles.description}>{gear.description || t('gearDetails.noDescription')}</div>
          <TranslateToggle text={gear?.description || ''} />

            {/* --- MOVED SPECIFICATIONS GRID --- */}
            <div className={styles.specsContainer}>
              <div className={styles.specsGridRight}>
                {/* Universal Specs */}
                {gear.gender && <div className={styles.specItem}><strong>Gender</strong> <span>{gear.gender}</span></div>}
                {gear.weight && <div className={styles.specItem}><strong>Weight</strong> <span>{gear.weight}g</span></div>}
                {gear.material_composition && <div className={styles.specItem}><strong>Material</strong> <span>{gear.material_composition}</span></div>}

                {/* Category Specific Specs */}
                {Object.entries(gear).map(([key, value]) => {
                  if (['id', 'name', 'brand', 'price', 'category', 'description', 'image', 'offers', 'validatedAt', 'originalId', 'gender', 'weight', 'material_composition', 'merchant', 'currency', 'link', 'breathability_metric'].includes(key)) return null;
                  const label = PROPERTY_LABELS[key];
                  if (!label) return null;

                  if (key === 'certifications') {
                    const certificationValues = Array.isArray(value)
                      ? value
                      : String(value || '').split(',').map((entry) => entry.trim()).filter(Boolean);

                    return (
                      <div key="certifications" className={styles.certificationsCard}>
                        <strong>{label}</strong>
                        <div className={styles.certTags}>
                          {certificationValues.map((certification, index) => (
                            <Link
                              key={`${key}-${index}-${certification}`}
                              to={`/gear/certifications#${getCertificationAnchorId(certification)}`}
                              className={styles.certTag}
                            >
                              {certification}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  const displayValue = key === 'breathability'
                    ? `${value}${gear.breathability_metric ? ` ${gear.breathability_metric}` : ''}`
                    : Array.isArray(value)
                      ? value.join(', ')
                      : value === true
                        ? 'Yes'
                        : value === false
                          ? 'No'
                          : value;
                  return (
                    <div key={key} className={styles.specItem}>
                      <strong>{label}</strong> <span>{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            </div>

        </section>

        <section className={styles.commentSection}>
          <div className={styles.commentHeader}>
            <h3>{t('gearDetails.commentsTitle')}</h3>
            <span className={styles.commentCount}>{t('gearDetails.entryCount', { count: comments.length })}</span>
          </div>
          {commentListError && (
            <div className={`${styles.error} ${styles.commentListError}`}>{commentListError}</div>
          )}
          {commentsLoading ? (
            <div className={styles.commentPlaceholder}>{t('gearDetails.loadingComments')}</div>
          ) : comments.length === 0 ? (
            <div className={styles.commentPlaceholder}>{t('gearDetails.noComments')}</div>
          ) : (
            <ul className={styles.commentList}>
              {comments.map((comment) => {
                const canDelete = comment.authorUsername === activeUsername || canModerateComments;
                const lastUpdatedLabel = formatCommentTimestamp(comment.updatedAt || comment.createdAt);
                return (
                  <li key={comment.id} className={styles.commentCard}>
                    <div className={styles.commentMeta}>
                      <div className={styles.commentAuthorBlock}>
                        <span className={styles.commentAuthor}>{comment.authorNickname || comment.authorUsername}</span>
                        <span className={styles.commentHandle}>@{comment.authorUsername}</span>
                      </div>
                      <span className={styles.commentTimestamp}>{lastUpdatedLabel}</span>
                    </div>
                    <p className={styles.commentBody}>{comment.text}</p>
                    {canDelete && (
                      <div className={styles.commentActions}>
                        {comment.authorUsername === activeUsername && (
                          <button
                            type="button"
                            className={styles.commentActionButton}
                            onClick={() => handleStartEditComment(comment)}
                          >
                            {t('gearDetails.commentEdit')}
                          </button>
                        )}
                        <button
                          type="button"
                          className={styles.commentActionButton}
                          onClick={() => handleDeleteComment(comment)}
                        >
                          {t('gearDetails.commentDelete')}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <form className={styles.commentForm} onSubmit={handleCommentSubmit}>
            <label htmlFor="gear-comment" className={styles.commentLabel}>
              {t(myComment ? 'gearDetails.commentUpdateLabel' : 'gearDetails.commentCreateLabel')}
            </label>
            <textarea
              id="gear-comment"
              ref={textareaRef}
              className={styles.commentTextarea}
              placeholder={activeUsername ? t('gearDetails.commentPlaceholder') : t('gearDetails.commentPlaceholderLoggedOut')}
              value={commentText}
              onChange={handleCommentInputChange}
              disabled={!activeUsername || commentSubmitting}
              maxLength={COMMENT_MAX_LENGTH}
            />
            <div className={styles.commentFormFooter}>
              <span className={styles.commentCharCount}>{commentText.length}/{COMMENT_MAX_LENGTH}</span>
              <button
                type="submit"
                className={styles.commentSubmitButton}
                disabled={!activeUsername || commentSubmitting}
              >
                {commentSubmitting
                  ? t('gearDetails.commentSaving')
                  : myComment
                    ? t('gearDetails.commentUpdate')
                    : t('gearDetails.commentPost')}
              </button>
            </div>
            {commentFormError && <div className={styles.error}>{commentFormError}</div>}
            {commentSuccess && <div className={styles.commentSuccess}>{commentSuccess}</div>}
            {!activeUsername && (
              <p className={styles.commentHint}>{t('gearDetails.commentHintLogin')}</p>
            )}
            {activeUsername && myComment && (
              <p className={styles.commentHint}>{t('gearDetails.commentHintEdit')}</p>
            )}
          </form>
        </section>
      </div>
      </div>

      {showAnalyticsPopup && effectiveAnalytics && (
        <div className={styles.analyticsPopupOverlay} onClick={() => setShowAnalyticsPopup(false)}>
          <div className={styles.analyticsPopupContent} onClick={e => e.stopPropagation()}>
            <button className={styles.closePopupBtn} onClick={() => setShowAnalyticsPopup(false)}>×</button>
            <h3>Detailed Price Analytics</h3>
            
            <div className={styles.analyticsGrid}>
              <div className={styles.analyticsCard}>
                <h4>Leaderboard</h4>
                <div className={styles.analyticsStat}>
                  <span>Most Often Cheapest:</span>
                  <strong>{effectiveAnalytics.leaderboard?.mostOftenCheapest || 'N/A'}</strong>
                </div>
                <div className={styles.analyticsStat}>
                  <span>Best Avg Price:</span>
                  <strong>{effectiveAnalytics.leaderboard?.bestAvgDiscount || 'N/A'}</strong>
                </div>
                {effectiveAnalytics.leaderboard?.bestAvgGapPercent != null && (
                  <div className={styles.analyticsStat}>
                    <span>Avg Gap from Lowest:</span>
                    <strong>{effectiveAnalytics.leaderboard.bestAvgGapPercent}%</strong>
                  </div>
                )}
              </div>
              
              <div className={styles.analyticsCard}>
                <h4>Current Week</h4>
                <div className={styles.analyticsStat}>
                  <span>Price Changes:</span>
                  <strong>{effectiveAnalytics.currentWeek?.volatility?.productChanges || 0}</strong>
                </div>
                <div className={styles.analyticsStat}>
                  <span>Price Range:</span>
                  <strong>
                    {effectiveAnalytics.currentWeek?.volatility?.productRange?.min != null ? formatCurrencyAmount(effectiveAnalytics.currentWeek.volatility.productRange.min, analyticsCurrency, i18n.language) : 'N/A'}
                    {' – '}
                    {effectiveAnalytics.currentWeek?.volatility?.productRange?.max != null ? formatCurrencyAmount(effectiveAnalytics.currentWeek.volatility.productRange.max, analyticsCurrency, i18n.language) : 'N/A'}
                  </strong>
                </div>
                {currentWeekCheapest && (
                  <div className={styles.analyticsStat}>
                    <span>Cheapest Seller:</span>
                    <strong>{formatCurrencyAmount(currentWeekCheapest.price, analyticsCurrency, i18n.language)} ({currentWeekCheapest.seller})</strong>
                  </div>
                )}
              </div>
            </div>

            {analyticsChartData.fullHistory.length > 1 && (
              <div className={styles.fullChartContainer}>
                <h4>Price History (per Seller)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsChartData.fullHistory}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis 
                      dataKey="weekKey" 
                      tick={{fontSize: 12, fill: '#666'}} 
                      tickFormatter={(val) => val.split('-')[1]} 
                    />
                    <YAxis 
                      domain={['auto', 'auto']} 
                      tick={{fontSize: 12, fill: '#666'}}
                      tickFormatter={(val) => formatCurrencyAmount(val, analyticsCurrency, i18n.language)}
                    />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrencyAmount(value, analyticsCurrency, i18n.language), name === 'lowestPrice' ? 'Overall Lowest' : name]}
                      labelFormatter={(label) => `Week: ${label}`}
                      contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="lowestPrice" 
                      name="Overall Lowest"
                      stroke="#007bff" 
                      strokeWidth={3}
                      dot={{r: 3}}
                      activeDot={{r: 5}}
                    />
                    {analyticsChartData.sellers.map((seller, idx) => (
                      <Line
                        key={seller}
                        type="monotone"
                        dataKey={seller}
                        name={seller}
                        stroke={SELLER_COLORS[idx % SELLER_COLORS.length]}
                        strokeWidth={1.5}
                        strokeDasharray="5 3"
                        dot={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                {analyticsChartData.sellers.length > 0 && (
                  <div className={styles.chartLegend}>
                    <span className={styles.legendItem}>
                      <span className={styles.legendSwatch} style={{backgroundColor: '#007bff'}} />
                      Overall Lowest
                    </span>
                    {analyticsChartData.sellers.map((seller, idx) => (
                      <span key={seller} className={styles.legendItem}>
                        <span className={styles.legendSwatch} style={{backgroundColor: SELLER_COLORS[idx % SELLER_COLORS.length]}} />
                        {seller}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {effectiveAnalytics.leaderboard?.priceStabilityRank?.length > 0 && (
              <div className={styles.analyticsCard} style={{marginBottom: '1.5rem'}}>
                <h4>Price Stability Ranking</h4>
                <p className={styles.stabilityHint}>Sellers ranked by fewest price changes (most stable first)</p>
                <ol className={styles.stabilityList}>
                  {effectiveAnalytics.leaderboard.priceStabilityRank.map((seller, idx) => (
                    <li key={seller} className={styles.stabilityItem}>
                      <span className={styles.stabilityRank}>#{idx + 1}</span>
                      <span className={styles.stabilitySeller}>{seller}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {effectiveAnalytics._synthetic && (
              <p className={styles.trackingNotice}>
                Price tracking recently started. Leaderboard, trust scores and history charts will appear as weekly data accumulates.
              </p>
            )}

            {effectiveAnalytics.trustScores && Object.keys(effectiveAnalytics.trustScores).length > 0 && (
              <div className={styles.trustScoresSection}>
                <h4>Seller Trust Scores</h4>
                <div className={styles.trustScoresList}>
                  {Object.entries(effectiveAnalytics.trustScores)
                    .sort(([, a], [, b]) => b - a)
                    .map(([seller, score]) => (
                    <div key={seller} className={styles.trustScoreItem}>
                      <span className={styles.trustScoreSeller}>{seller}</span>
                      <div className={styles.trustScoreBarContainer}>
                        <div 
                          className={styles.trustScoreBar} 
                          style={{
                            width: `${score}%`,
                            backgroundColor: score > 80 ? '#28a745' : score > 50 ? '#ffc107' : '#dc3545'
                          }}
                        />
                      </div>
                      <span className={styles.trustScoreValue}>{score}/100</span>
                    </div>
                  ))}
                </div>
                <p className={styles.trustScoreHint}>
                  Trust scores factor in pricing outliers, volatility, data availability, freshness and promotion accuracy.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

    </>
  );
}

export default GearDetailsPage;
